import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import TextField from './TextField';
import { color_colors_decline, color_shades_darkest, color_variants_decline_light_1 } from '../../constants/colors';

const alertIcon = require('../../content/images/alert-circle.svg');

const PHONE_ERROR_MESSAGE = 'Phone number must be 11 digits, start with 1, and contain digits only';

/**
 * Returns true when the value is a non-empty phone number input that either
 * contains non-digit characters or does not satisfy the 11-digit,
 * starts-with-1 format.
 * Empty strings and undefined are allowed through so the backend can receive
 * "null" / empty values when needed.
 */
export const isInvalidPhone = (value: string | undefined): boolean => {
    if (value === undefined || value === '') return false;
    // Non-digit characters (e.g. templated values) are intentionally allowed through without validation
    if (!/^\d+$/.test(value)) return false;
    // A purely numeric value must be exactly 11 digits starting with 1
    return !/^1\d{10}$/.test(value);
};

interface PhoneNumberFormFieldProps {
    name?: string;
    label?: string;
    placeholder?: string;
    value?: string;
    defaultValue?: string;
    onChange: (value: string) => void;
    onReset?: () => void;
    onValidationChange?: (hasErrors: boolean) => void;
}

const PhoneNumberFormField: React.FC<PhoneNumberFormFieldProps> = (props) => {
    const [hasBlurred, setHasBlurred] = useState(false);
    const prevHasErrorRef = useRef(false);

    // Derive the error state directly from the value prop so it stays in
    // sync regardless of how or when React fires the blur / change events.
    const currentValue = props.value ?? props.defaultValue;
    const hasError = hasBlurred && isInvalidPhone(currentValue || undefined);

    // Notify parent whenever the derived error state changes
    useEffect(() => {
        if (prevHasErrorRef.current !== hasError) {
            prevHasErrorRef.current = hasError;
            if (props.onValidationChange) {
                props.onValidationChange(hasError);
            }
        }
    }, [hasError]);

    const handleBlur = useCallback(() => {
        setHasBlurred(true);
    }, []);

    return (
        <div>
            <TextField
                name={props.name}
                value={props.value}
                defaultValue={props.defaultValue}
                type="tel"
                useForm={false}
                onChange={(e) => props.onChange(e.target.value)}
                onBlur={handleBlur}
                onReset={props.onReset}
                label={props.label}
                placeholder={props.placeholder}
            />
            {hasError && (
                <ErrorStyle>
                    <AlertIconStyle src={alertIcon} />
                    {PHONE_ERROR_MESSAGE}
                </ErrorStyle>
            )}
        </div>
    );
};

const ErrorStyle = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    font-family: Muli, "sans-serif";
    font-weight: normal;
    color: ${color_shades_darkest};
    background-color: ${color_variants_decline_light_1};
    border: 1px solid ${color_colors_decline};
    border-radius: 8px;
    flex-grow: 1;
    padding: 8px 16px;
    margin: 8px 0;
    font-size: 14px;
`;

const AlertIconStyle = styled.img`
    margin-right: 8px;
`;

export default PhoneNumberFormField;
